import React, {
  ComponentType,
  createContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useCountryMarketState } from '../controllers/CountryMarketController/CountryMarketState';
import { findRouteByPathName } from '../lib/utils';
import { routes } from '../routes';
import { Routes } from '../types';

const basePath = (name: string) => {
  const result = findRouteByPathName(routes, name);

  return result?.basePath?.path;
};

const initialRoutes = {
  introRoute: basePath('intro'),
  signInRoute: basePath('signIn'),
  briefingSelectionRoute: basePath('briefingSelection'),
  stepSuccessRoute: basePath('stepSuccess'),
  briefingRoute: basePath('briefing'),
  projectOverviewRoute: basePath('projectOverview'),
  accountRoute: basePath('account'),
  oauthHandler: basePath('oauthHandler'),
};

enum ActionType {
  SET_ROUTES,
}

type Action = { type: ActionType.SET_ROUTES; routes: Routes };

export const RoutesContext = createContext<Routes>(initialRoutes);

const reducer = (state: Routes, action: Action): Routes => {
  switch (action.type) {
    case ActionType.SET_ROUTES:
      return {
        ...state,
        ...action.routes,
      };
    default:
      return state;
  }
};

type RoutesProviderProps = {
  children: React.ReactNode;
};

export const RoutesProvider: ComponentType<RoutesProviderProps> = ({
  children,
}: RoutesProviderProps) => {
  const [{ countryMarketConfig }] = useCountryMarketState();

  const marketCountryURL = useMemo(
    () =>
      `/${countryMarketConfig?.eLabCountrySlug}/${countryMarketConfig?.eLabMarketSlug}`,
    [countryMarketConfig],
  );

  const accessRoutes = useMemo(
    () => ({
      introRoute: basePath('intro'),
      oauthHandler: basePath('oauthHandler'),
      briefingSelectionRoute: `${marketCountryURL}${basePath(
        'briefingSelection',
      )}`,
      stepSuccessRoute: `${marketCountryURL}${basePath('stepSuccess')}`,
      briefingRoute: `${marketCountryURL}${basePath('briefing')}`,
      projectOverviewRoute: `${marketCountryURL}${basePath('projectOverview')}`,
      accountRoute: `${marketCountryURL}${basePath('account')}`,
    }),
    [marketCountryURL],
  );

  const [stateRoutes, dispatch] = useReducer(reducer, accessRoutes);

  useEffect(() => {
    dispatch({
      type: ActionType.SET_ROUTES,
      routes: accessRoutes,
    });
  }, [accessRoutes, countryMarketConfig]);

  return (
    <RoutesContext.Provider value={stateRoutes}>
      {children}
    </RoutesContext.Provider>
  );
};
